import {
  Button,
  Grid,
  IconButton,
  InputBase,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import React from 'react';
import update from 'react-addons-update';
import { Link } from 'react-router-dom';

import {
  BrandSafetyRuleActiveActive,
  BrandSafetyRuleActiveInactive,
  BrandSafetyRuleTargetingTypeBidder,
  BrandSafetyRuleTargetingTypeBidderGroup,
  BrandSafetyRuleTargetingTypeChannel,
  BrandSafetyRuleTargetingTypeChannelGroup,
  BrandSafetyRuleTargetingTypeGlobal,
  IBrandSafetyRule,
  targetingTypeToTKey,
} from '~/src/dash/api/brand-safety-rules';
import { CreativeReview } from '~/src/dash/views/brand-safety/creative-review';
import {
  BrandSafetyTrackerActiveActive,
  BrandSafetyTrackerActiveInactive,
  getBrandSafetyTrackers,
  getTrackerEventTargeting,
  getTrackerThirdParty,
  IBrandSafetyTracker,
  updateBrandSafetyTracker,
} from '~/src/dash/api/brand-safety-tracker';
import { REVIEW_STATUS_UNREVIEWED } from '~/src/dash/api/creative-validation';
import { createUrl, IHttpResponse, restDelete, restGet, restPatch } from '~/src/dash/api/rest';
import { ICreativeValidationRowData } from '~/src/dash/components/list-table';
import { ModelDialogDeleteView } from '~/src/dash/components/model-dialog-delete-view';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import { ct, Panel, Panels, spacing } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteBrandSafetyEdit,
  RouteBrandSafetyNew,
  RouteBrandSafetyTrackerEdit,
  RouteBrandSafetyTrackerNew,
  setStringFromParam,
} from '~/src/dash/router/routes';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { getRuleTypeChip } from '~/src/dash/utils/brand-safety-chip';
import { decorate } from '~/src/dash/utils/decorator';
import { IHeadCell } from '~/src/dash/utils/headCell';
import { deleteColor } from '~/src/dash/utils/material-dashboard-react';
import {
  IPermissionCheck,
  IS_AUTHORIZED,
  notAllowedEmptyIPermissionCheck,
} from '~/src/dash/utils/permission_service';
import { checkUserInfoMasked } from '~/src/dash/utils/roles';
import { RowButton } from '~/src/dash/views/component-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { TopBar } from '~/src/dash/views/topbar/TopBar';
import { SimpleTabs } from '~/src/dash/components/tabs/SimpleTabs';
import { ISimpleTab } from '~/src/dash/components/tabs/common';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState extends IBaseState {
  // rule list states
  loading: boolean;
  loadingTrackers: boolean;
  updatingReviewOrderby: boolean;
  updatingReviewStatus: boolean;
  // queryParams state
  queryParams: URLSearchParams;
  // delete popup
  deletePopupActive: boolean;
  deletePopupItemID: string;
  deletePopupItemName: string;
  // rules list
  rules: IBrandSafetyRule[];
  // snackbar - used to display small notification
  snackbarMessage: string; // message → the snackbar will automatically be displayed if this message is not empty
  snackbarError: boolean; // error → will turn the snackbar red
  // list sorting
  orderDirection: 'asc' | 'desc';
  orderBy: string;
  // search
  searchFilter: string; // user search query
  // pagination
  page: number; // current page number
  pageTrackers: number; // current page number
  rowsPerPage: number; // number of entries per page
  rowsPerPageTrackers: number; // number of entries per page
  // permissions
  hasPublisherReadRight: IPermissionCheck;
  hasPublisherWriteRight: IPermissionCheck;
  hasChannelReadRight: IPermissionCheck;
  hasChannelWriteRight: IPermissionCheck;
  hasBidderReadRight: IPermissionCheck;
  hasBidderWriteRight: IPermissionCheck;
  // trackers list
  trackers: IBrandSafetyTracker[];
  // creative_validation list related fields
  adomainFilter: string;
  categoryFilter: string;
  creativeURLFilter: string;
  reviewStatusFilter: number;
  siteFilter: string;
  bidderFilter: string;
  bidderTierFilter: string;
  demandSourceFilter: string;
  openEditCreativeReviewModal: boolean;
  updatingCreativeReview: boolean;
  // IAB Category
  IABCategoryPrefix: string;
  IABCategorySuffix: string;
  IABSubCategoryList: IMenuList;
  // current tab
  currentTab: number;
  creativeData: ICreativeValidationRowData;
}

// -------------------- COMPONENT DECLARATION --------------------
export const BrandSafetyListView = decorate(
  {
    // ---- REDUX
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    // ---- STYLE DECLARATION
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
        overflow: 'scroll',
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      tooltipSmall: {
        fontSize: 10,
      },
      tooltipBold: {
        fontSize: 12,
        fontWeight: 500,
      },
      textField: {
        margin: 0,
        minWidth: '400px',
        width: '30%',
      },
      mainTab: {
        '&:hover': {
          background: '#d7d8fc 0% 0% no-repeat padding-box',
          borderRadius: '5px',
          color: '#4952FF',
          fontSize: '14px',
          fontWeight: '500',
        },
        color: '#707070',
        textDecoration: 'none',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
      },
      mainTabSelected: {
        background: '#d7d8fc 0% 0% no-repeat padding-box',
        borderRadius: '5px',
        color: '#4952FF',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
      },
      input: {
        width: '85%',
      },
      toolbarRow: {
        marginLeft: -10,
        width: '100%',
        marginTop: 5,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    queryParams = new URLSearchParams(this.props.location.search);
    hasPresetID = !!this.queryParams.get('site_id') || !!this.queryParams.get('bidder_id');
    // STATE INITIALIZATION
    state = {
      // rule list states
      loading: false,
      loadingTrackers: false,
      updatingReviewOrderby: false,
      updatingReviewStatus: false,
      // queryParams state
      queryParams: this.queryParams,
      // current tab
      currentTab: this.hasPresetID ? 2 : 0,
      // delete rule popup
      deletePopupActive: false,
      deletePopupItemID: '',
      deletePopupItemName: '',
      // rules list
      rules: [] as IBrandSafetyRule[],
      // snackbar - used to display small notification
      snackbarMessage: '',
      snackbarError: false,
      // list sorting
      orderDirection: 'asc' as 'asc' | 'desc',
      orderBy: 'Status',
      // search
      searchFilter: '', // user search query
      // pagination
      page: 0, // current page
      rowsPerPage: 10, // number of entries per page
      pageTrackers: 0, // current page
      rowsPerPageTrackers: 10, // number of entries per page
      // permissions
      hasPublisherReadRight:
        notAllowedEmptyIPermissionCheck /* todo uncomment to release global targeting - IS_AUTHORIZED(['read_publisher_brandsafety_rule'], this.props.user.user)*/,
      hasPublisherWriteRight:
        notAllowedEmptyIPermissionCheck /* todo uncomment to release global targeting - IS_AUTHORIZED(['write_publisher_brandsafety_rule'], this.props.user.user)*/,
      hasChannelReadRight: IS_AUTHORIZED(['read_channel_brandsafety_rule'], this.props.user.user),
      hasChannelWriteRight: IS_AUTHORIZED(['write_channel_brandsafety_rule'], this.props.user.user),
      hasBidderReadRight: IS_AUTHORIZED(['read_channel_brandsafety_rule'], this.props.user.user),
      hasBidderWriteRight: IS_AUTHORIZED(['write_channel_brandsafety_rule'], this.props.user.user),
      trackers: [] as IBrandSafetyTracker[],

      userList: {} as IMenuList,

      // Filters
      adomainFilter: '',
      categoryFilter: '',
      creativeURLFilter: '',
      reviewStatusFilter: REVIEW_STATUS_UNREVIEWED,
      siteFilter: setStringFromParam(this.props.location.search, 'site_id'),
      bidderFilter: setStringFromParam(this.props.location.search, 'bidder_id'),
      bidderTierFilter: '',
      demandSourceFilter: '',
      userFilter: '',
      openEditCreativeReviewModal: false,
      updatingCreativeReview: false,
      // IAB Category
      IABCategoryPrefix: '',
      IABCategorySuffix: '',
      IABSubCategoryList: {} as IMenuList,
      creativeData: {} as ICreativeValidationRowData,
    };

    // initialization → executed after this component mounts
    componentDidMount() {
      // if rules are defined in the session, load them before doing the query to the API
      this.getRulesFromSession();
      // then make the actual query to the API to refresh changes
      this.getRulesFromAPI();
      // get the trackers
      this.getTrackers();
    }

    // get trackers
    getTrackers = () => {
      getBrandSafetyTrackers(this.props.session.publisher.ID)
        .then((val: IHttpResponse<IBrandSafetyTracker[]>) => {
          this.setState({
            trackers: val.parsedBody as IBrandSafetyTracker[],
          });
        })
        .catch(() => {
          this.setState({
            trackers: [] as IBrandSafetyTracker[],
            snackbarError: true,
            snackbarMessage: 'Error while getting the list of trackers from the API.',
          });
        });
    };

    // get the rules list from the session variable
    getRulesFromSession() {
      if (this.props.session !== undefined && this.props.session.brandSafetyRules !== undefined) {
        this.setState({
          brandSafetyRules: this.props.session.brandSafetyRules,
        });
      }
    }

    // get the rules list from the console API
    getRulesFromAPI() {
      // build request URL
      const reqURL = createUrl('/brandsafetyrules', this.props.session.publisher.ID);
      // make the request
      restGet<IBrandSafetyRule[]>(reqURL, {})
        .then((val: IHttpResponse<IBrandSafetyRule[]>) => {
          const apiRules = val.parsedBody as IBrandSafetyRule[];
          // OK
          this.setState({
            rules: apiRules,
          });
          this.props.session.brandSafetyRules = apiRules;
        })
        .catch(() => {
          // error
          this.setState({
            rules: [] as IBrandSafetyRule[],
            snackbarError: true,
            snackbarMessage: 'Error while getting the list of rules from the API.',
          });
        });
    }

    // update an already existing rule in the API
    // if ruleIndex is defined → update the rule in the state if success
    updateRuleInAPI(rule: IBrandSafetyRule, ruleIndex: number) {
      const rules = [...this.state.rules];
      // build request URL
      const reqURL = createUrl(`/brandsafetyrules/${rule.ID}`, this.props.session.publisher.ID);
      // make the request
      restPatch<IBrandSafetyRule>(reqURL, rule)
        .then(() => {
          // ok → update rule in state
          rules[ruleIndex] = rule;
          this.setState({
            rules,
            snackbarError: false,
            snackbarMessage: ct('SnackbarItemUpdatedSuccessfully', { item: 'Rule' }),
          });
        })
        .catch(() => {
          // error
          this.setState({
            snackbarError: true,
            snackbarMessage: ct('SnackbarFailedToUpdateItem', { item: 'Rule' }),
          });
        });
    }

    // return a promise that deletes a rule or tracker in the API
    deleteItemWithPromise(itemID: number) {
      let reqURL;
      // build request URL
      if (this.isCurrentTab(0)) {
        reqURL = createUrl(`/brandsafetyrules/${itemID}`, this.props.session.publisher.ID);
      } else {
        reqURL = createUrl(`/brand_safety_tracker/${itemID}`, this.props.session.publisher.ID);
      }
      return restDelete<IBrandSafetyTracker | IBrandSafetyRule>(reqURL, null);
    }

    // handles the rules / tracker filter change (when user change the search text field)
    handleFilterChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({
        searchFilter: target.value, // change user query
        page: 0, // reset pagination to 0
      });
    };

    // handles the rule's active switch change
    handleActiveSwitchChange = (item: IBrandSafetyRule | IBrandSafetyTracker, checked: boolean) => {
      if (this.isCurrentTab(0)) {
        const index = this.state.rules.indexOf(item as IBrandSafetyRule);
        const updatedItem = { ...item } as IBrandSafetyRule;
        updatedItem.Active = checked ? BrandSafetyRuleActiveActive : BrandSafetyRuleActiveInactive;
        this.updateRuleInAPI(updatedItem as IBrandSafetyRule, index);
      } else {
        const index = this.state.trackers.indexOf(item as IBrandSafetyTracker);
        const updatedItem = { ...item } as IBrandSafetyTracker;
        updatedItem.Active = checked
          ? BrandSafetyTrackerActiveActive
          : BrandSafetyTrackerActiveInactive;
        updateBrandSafetyTracker(updatedItem)
          .then((val: IHttpResponse<IBrandSafetyTracker>) => {
            const trackers = this.state.trackers;
            trackers[index] = val.parsedBody as IBrandSafetyTracker;
            this.setState({
              trackers,
              snackbarError: false,
              snackbarMessage: `Tracker '${item.Name}' updated`,
            });
          })
          .catch(() => {
            this.setState({
              snackbarError: true,
              snackbarMessage: `Error while updating tracker '${item.Name}'`,
            });
          });
      }
    };

    // handle sorting
    handleRequestSort = (property: string) => {
      const isAsc = this.state.orderBy === property && this.state.orderDirection === 'asc';
      this.setState({
        orderDirection: isAsc ? 'desc' : 'asc',
        orderBy: property,
      });
    };

    // return the route path to navigate to the brand blocked settings
    getRuleSettingsRoute = (rule: IBrandSafetyRule): string => {
      return getRoutePath(RouteBrandSafetyEdit, extractRouteParams(this.props), {
        brandSafetyRuleID: String(rule.ID),
      });
    };

    // handles click on rule's name → navigates to the edition page for this rule
    editRule = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      this.props.session.brandSafetyReferrer = 'brandsafety-list'; // set referrer for the edit view 'close' button
      navigate(RouteBrandSafetyEdit, extractRouteParams(this.props), {
        brandSafetyRuleID: currentTarget.value,
      });
    };

    // return the route path to navigate to the brand tracker's settings
    getTrackerSettingsRoute = (tracker: IBrandSafetyTracker): string => {
      return getRoutePath(RouteBrandSafetyTrackerEdit, extractRouteParams(this.props), {
        brandSafetyTrackerID: String(tracker.ID),
      });
    };

    // handles click on tracker's name → navigates to the edition page for this tracker
    editTracker = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      navigate(RouteBrandSafetyTrackerEdit, extractRouteParams(this.props), {
        brandSafetyTrackerID: currentTarget.value,
      });
    };

    // handles the deletion of a rule
    showDeleteRulePopup = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      const id = currentTarget.value;
      const item = this.findItemByID(Number(id));
      if (item) {
        this.setState({
          deletePopupActive: true,
          deletePopupItemID: String(item.ID),
          deletePopupItemName: item.Name,
        });
      }
    };

    // handles the page change for the rules list pagination
    handleTablePaginationPageChange = (_: any, page: number) => {
      if (this.isCurrentTab(0)) {
        this.setState({
          page,
        });
      } else {
        this.setState({
          pageTrackers: page,
        });
      }
    };

    // handle change in number of row per page for table pagination
    handleTablePaginationRowsPerPageChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (this.isCurrentTab(0)) {
        this.setState({
          page: 0,
          rowsPerPage: parseInt(event.target.value, 10),
        });
      } else {
        this.setState({
          pageTrackers: 0,
          rowsPerPageTrackers: parseInt(event.target.value, 10),
        });
      }
    };

    // handle the rule entries filtering related to the user search query
    filterRules = (rules: IBrandSafetyRule[]) => {
      return rules.filter((rule: IBrandSafetyRule) => {
        return !(
          !rule.Name.toLowerCase().includes(this.state.searchFilter.toLowerCase()) &&
          !rule.ID.toString().includes(this.state.searchFilter)
        );
      });
    };

    // handle the tracker entries filtering related to the user search query
    filterTrackers = (trackers: IBrandSafetyTracker[]) => {
      return trackers.filter((tracker: IBrandSafetyTracker) => {
        return !(
          !tracker.Name.toLowerCase().includes(this.state.searchFilter.toLowerCase()) &&
          !tracker.ID.toString().includes(this.state.searchFilter)
        );
      });
    };

    // '+ Add Rule' button click handler
    addRuleButtonClickHandler = () => {
      navigate(
        RouteBrandSafetyNew,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    // '+ Add Tracker' button click handler
    addTrackerButtonClickHandler = () => {
      navigate(
        RouteBrandSafetyTrackerNew,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    // function that handle the hiding of the delete popup
    hideDeleteDialog = () => {
      this.setState({
        deletePopupActive: false,
      });
    };

    // function called when the item has been deleted with success (after delete popup)
    // delete the tracker or rule locally in the state tracker or rule list
    onItemDelete = (itemIDStr: string) => {
      const itemID = Number(itemIDStr);
      let itemIndex;
      // find index of rule in state rule array
      if (this.isCurrentTab(0)) {
        itemIndex = this.state.rules.findIndex((rule: IBrandSafetyRule) => rule.ID === itemID);
        // delete item at index in array
        this.setState({
          rules: update(this.state.rules, { $splice: [[itemIndex, 1]] }),
          deletePopupActive: false,
        });
      } else {
        itemIndex = this.state.trackers.findIndex(
          (tracker: IBrandSafetyTracker) => tracker.ID === itemID,
        );
        // delete item at index in array
        this.setState({
          trackers: update(this.state.trackers, { $splice: [[itemIndex, 1]] }),
          deletePopupActive: false,
        });
      }
    };

    // function called when a snackbar notification is closed
    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
      });
    };

    // returns a string describing the channel/bidder targeting of a rule
    GetRuleTargetingString(rule: IBrandSafetyRule): string {
      // handle global
      if (rule.RuleTargetingType === BrandSafetyRuleTargetingTypeGlobal) {
        return 'Global';
      }
      // write message
      let message = ct('FormatLabelWithCount', {
        count: rule.TargetedObjects.length,
        label: ct(targetingTypeToTKey(rule.RuleTargetingType)),
      });

      // add excluded if the results are excluded
      if (rule.IsTargetingExcluded) {
        message += ` - (${ct('Exclude')})`;
      }
      return message;
    }

    // returns a string containing the rule type (IAB or Adomain or Mediafile}
    GetRuleTypeString(rule: IBrandSafetyRule): string {
      if (rule.TargetedIABs) {
        return ct('IAB');
      }
      if (rule.TargetedAdomains) {
        return ct('Adomain');
      }
      if (rule.TargetedMediafiles) {
        return ct('Mediafile');
      }
      if (rule.TargetedCreativeIDs) {
        return ct('CreativeID');
      }
      if (rule.TargetedLanguages) {
        return ct('Language');
      }
      return ct('Unknown');
    }

    // returns the item that correspond to the given ID
    findItemByID(itemID: number): IBrandSafetyRule | IBrandSafetyTracker | undefined {
      if (this.isCurrentTab(0)) {
        return this.state.rules.find((rule: IBrandSafetyRule) => rule.ID === itemID);
      } else {
        return this.state.trackers.find((tracker: IBrandSafetyTracker) => tracker.ID === itemID);
      }
    }

    // sort the rule list accordingly to what the user choose on the table
    sortRuleList(ruleList: IBrandSafetyRule[]) {
      ruleList.sort((a: IBrandSafetyRule, b: IBrandSafetyRule) => {
        // get rule targeting
        const aTargetingStr = this.GetRuleTargetingString(a);
        const bTargetingStr = this.GetRuleTargetingString(b);
        // get rule type
        const aTypeStr = this.GetRuleTypeString(a);
        const bTypeStr = this.GetRuleTypeString(b);
        switch (this.state.orderBy) {
          case 'IsActive':
            return (this.state.orderDirection === 'asc' ? -1 : 1) * (a.Active - b.Active);
          case 'Name':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (a.Name === b.Name ? 0 : a.Name > b.Name ? 1 : -1)
            );
          case 'Type':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (aTypeStr === bTypeStr ? 0 : aTypeStr > bTypeStr ? 1 : -1)
            );
          case 'Targeting':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (aTargetingStr === bTargetingStr ? 0 : aTargetingStr > bTargetingStr ? 1 : -1)
            );
          case 'NbrResponseBlocked':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (a.NbrResponseBlocked - b.NbrResponseBlocked)
            );
          default:
            return 0; // don't sort
        }
      });
      return ruleList;
    }

    // sort the tracker list accordingly to what the user choose on the table
    sortTrackerList(trackerList: IBrandSafetyTracker[]) {
      trackerList.sort((a: IBrandSafetyTracker, b: IBrandSafetyTracker) => {
        // get rule targeting
        const aTargetingStr = getTrackerEventTargeting(a);
        const bTargetingStr = getTrackerEventTargeting(b);
        switch (this.state.orderBy) {
          case 'Active':
            return (this.state.orderDirection === 'asc' ? -1 : 1) * (a.Active - b.Active);
          case 'Name':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (a.Name === b.Name ? 0 : a.Name > b.Name ? 1 : -1)
            );
          case 'ThirdParty':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (a.ThirdParty === b.ThirdParty ? 0 : a.ThirdParty > b.ThirdParty ? 1 : -1)
            );
          case 'Events':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (aTargetingStr === bTargetingStr ? 0 : aTargetingStr > bTargetingStr ? 1 : -1)
            );
          case 'MeasuredInventory':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (a.MeasuredInventory - b.MeasuredInventory)
            );
          default:
            return 0; // don't sort
        }
      });
      return trackerList;
    }

    isRuleModificationAllowed(rule: IBrandSafetyRule): boolean {
      switch (rule.RuleTargetingType) {
        case BrandSafetyRuleTargetingTypeGlobal:
          return this.state.hasPublisherWriteRight.isAuthorized;
        case BrandSafetyRuleTargetingTypeChannel:
        case BrandSafetyRuleTargetingTypeChannelGroup:
          return this.state.hasChannelWriteRight.isAuthorized;
        case BrandSafetyRuleTargetingTypeBidder:
        case BrandSafetyRuleTargetingTypeBidderGroup:
          return this.state.hasBidderWriteRight.isAuthorized;
      }
      return false;
    }

    // RENDERING FUNCTIONS

    // ############################## RENDER LIST ROWS WHEN EMPTY ##############################
    renderEmptyRulesList() {
      return (
        <TableRow>
          <TableCell colSpan={4}>{ct('NoDataFound')}</TableCell>
        </TableRow>
      );
    }

    // ############################## RENDER LIST ROWS WHEN LOADING ##############################
    renderLoadingRulesList() {
      return (
        <TableRow>
          <TableCell colSpan={4}>{ct('LoadingEllipsis')}</TableCell>
        </TableRow>
      );
    }

    // ############################## RENDER LIST ROWS WHEN FILLED ##############################
    renderFilledRulesList() {
      // rules filtering → filter rules according to the user 'search' input
      const filteredRules = this.filterRules(this.state.rules);
      // rules sorting → sort rules according to what the user choose to sort in the rules list component
      const sortedRules = this.sortRuleList(filteredRules);
      // rules pagination
      const paginatedRules = sortedRules.slice(
        this.state.page * this.state.rowsPerPage,
        (this.state.page + 1) * this.state.rowsPerPage,
      );

      // display only rules that permissions allow to
      const displayedRules = [] as IBrandSafetyRule[];
      paginatedRules.forEach((rule: IBrandSafetyRule) => {
        // global
        if (
          this.state.hasPublisherReadRight.isAuthorized &&
          rule.RuleTargetingType === BrandSafetyRuleTargetingTypeGlobal
        ) {
          displayedRules.push(rule);
        }
        // channel
        if (
          this.state.hasChannelReadRight.isAuthorized &&
          (rule.RuleTargetingType === BrandSafetyRuleTargetingTypeChannel ||
            rule.RuleTargetingType === BrandSafetyRuleTargetingTypeChannelGroup)
        ) {
          displayedRules.push(rule);
        }
        // bidder
        if (
          this.state.hasBidderReadRight.isAuthorized &&
          (rule.RuleTargetingType === BrandSafetyRuleTargetingTypeBidder ||
            rule.RuleTargetingType === BrandSafetyRuleTargetingTypeBidderGroup)
        ) {
          displayedRules.push(rule);
        }
      });

      // actual rendering of rows
      return displayedRules.map((row: IBrandSafetyRule) => {
        // get the type chip for this rule
        return (
          <TableRow key={'r_' + row.ID}>
            {/* ACTIVE / INACTIVE */}
            <TableCell align='center'>
              <div>
                <Switch
                  disabled={!this.isRuleModificationAllowed(row)}
                  checked={row.Active === BrandSafetyRuleActiveActive}
                  onChange={({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
                    this.handleActiveSwitchChange(row, currentTarget.checked);
                  }}
                  color='primary'
                />
              </div>
            </TableCell>

            {/* NAME */}
            <TableCell align='center'>
              {/* button that handle click on rule's name to go to its settings */}
              <Button component={Link} to={this.getRuleSettingsRoute(row)}>
                <div
                  style={{
                    textAlign: 'left',
                  }}
                >
                  {/* display rule name */}
                  <strong>
                    {checkUserInfoMasked(this.props.user.user) ? 'demo_rule_' + row.ID : row.Name}
                  </strong>
                </div>
              </Button>
            </TableCell>

            {/* TYPE CHIP */}
            <TableCell align='center'>{this.renderTypeChip(row)}</TableCell>

            {/* TARGETING */}
            <TableCell align='center'>{this.GetRuleTargetingString(row)}</TableCell>

            {/* RESPONSE BLOCKED */}
            <TableCell align='center'>{row.NbrResponseBlocked}</TableCell>

            {/* DELETE */}
            <TableCell align='center'>
              <Button
                disabled={!this.isRuleModificationAllowed(row)}
                onClick={this.showDeleteRulePopup}
                value={row.ID}
              >
                <DeleteIcon
                  style={{
                    fill: this.isRuleModificationAllowed(row) ? deleteColor : '#d9d9d9',
                  }}
                />
              </Button>
            </TableCell>
          </TableRow>
        );
      });
    }

    // ############################## RENDER LIST ROWS WHEN FILLED ##############################
    renderFilledTrackersList() {
      // trackers filtering → filter trackers according to the user 'search' input
      const filteredTrackers = this.filterTrackers(this.state.trackers);
      // trackers sorting → sort trackers according to what the user choose to sort in the trackers list component
      const sortedTrackers = this.sortTrackerList(filteredTrackers);
      // trackers pagination
      const paginatedTrackers = sortedTrackers.slice(
        this.state.pageTrackers * this.state.rowsPerPageTrackers,
        (this.state.pageTrackers + 1) * this.state.rowsPerPageTrackers,
      );

      // actual rendering of rows
      return paginatedTrackers.map((row: IBrandSafetyTracker) => {
        const eventsString = getTrackerEventTargeting(row);
        return (
          <TableRow key={'r_' + row.ID}>
            {/* ACTIVE / INACTIVE */}
            <TableCell align='center'>
              <div>
                <Switch
                  checked={row.Active === BrandSafetyTrackerActiveActive}
                  onChange={({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
                    this.handleActiveSwitchChange(row, currentTarget.checked);
                  }}
                  color='primary'
                />
              </div>
            </TableCell>

            {/* NAME */}
            <TableCell align='center'>
              {/* button that handle click on tracker's name to go to its settings */}
              <Button component={Link} to={this.getTrackerSettingsRoute(row)}>
                <div
                  style={{
                    textAlign: 'left',
                  }}
                >
                  {/* display tracker name */}
                  <strong>
                    {checkUserInfoMasked(this.props.user.user)
                      ? 'demo_tracker_' + row.ID
                      : row.Name}
                  </strong>
                </div>
              </Button>
            </TableCell>

            {/* THIRD PARTY */}
            <TableCell align='center'>{getTrackerThirdParty(row)}</TableCell>

            {/* EVENTS */}
            <TableCell align='center'>{eventsString}</TableCell>

            {/* SCALE / Measured Inventory */}
            <TableCell align='center'>{row.MeasuredInventory}</TableCell>

            {/* DELETE */}
            <TableCell align='center'>
              <Button onClick={this.showDeleteRulePopup} value={row.ID}>
                <DeleteIcon
                  style={{
                    fill: deleteColor,
                  }}
                />
              </Button>
            </TableCell>
          </TableRow>
        );
      });
    }

    // ############################## RENDER EACH ROWS TYPE CHIP ##############################
    renderTypeChip(rule: IBrandSafetyRule) {
      const chip = getRuleTypeChip(rule);
      const ChipComponent = chip.component;
      return <ChipComponent label={chip.label} style={{ alignItems: 'right' }} />;
    }

    // ############################## RENDER LIST ROWS ##############################
    renderListRows() {
      if (this.isCurrentTab(0)) {
        // if loading
        if (this.state.loading) {
          return this.renderLoadingRulesList();
        }
        // if empty
        if (this.state.rules.length === 0) {
          return this.renderEmptyRulesList();
        }
        // if not empty
        return this.renderFilledRulesList();
      } else {
        // if loading
        if (this.state.loadingTrackers) {
          return this.renderLoadingRulesList();
        }
        // if empty
        if (this.state.trackers.length === 0) {
          return this.renderEmptyRulesList();
        }
      }
      // if not empty
      return this.renderFilledTrackersList();
    }

    // ************************************
    // ********* TABLE FUNCTIONS **********
    // ************************************

    // isCurrentTab receives one argument and compares that against the `currentTab` in state
    isCurrentTab = (tabToCheck: number): boolean => {
      const { currentTab } = this.state;
      return currentTab === tabToCheck;
    };

    // handleChangeTab is passed to the SimpleTab component to set the current tab
    handleChangeTab = (_event: React.SyntheticEvent, index: number): void => {
      this.setState({
        currentTab: index,
      });
    };

    // ************************************
    // ********* RENDER FUNCTIONS *********
    // ************************************

    // renderTabs renders the `All` and `Groups` tabs for the Channels view
    // depending on the value of `currentTab` stored in the state
    renderTabs() {
      const tabs: ISimpleTab[] = [
        { key: 'Brand Safety Rules', label: ct('BrandSafetyRule'), value: 0 },
        { key: 'Event Trackers', label: ct('EventTracker'), value: 1 },
        { key: 'Creative Review', label: ct('CreativeReview'), value: 2 },
      ];

      // define the rules list table columns (like active, name, type ... etc.)
      const rulesListColumns: IHeadCell[] = [
        { id: 'IsActive', numeric: false, label: ct('Active') },
        { id: 'Name', numeric: false, label: ct('Name') },
        { id: 'Type', numeric: false, label: ct('Type') },
        { id: 'Targeting', numeric: false, label: ct('Targeting') },
        { id: 'NbrResponseBlocked', numeric: true, label: ct('ResponsesBlocked') },
        // the delete button column is automatically added at the end of those
      ];

      // define the tracker list table columns (like active, name, ... etc.)
      const trackerListColumns: IHeadCell[] = [
        { id: 'Active', numeric: false, label: 'Active' },
        { id: 'Name', numeric: false, label: 'Name' },
        { id: 'ThirdParty', numeric: false, label: 'ThirdParty' },
        { id: 'Events', numeric: false, label: 'Events' },
        { id: 'MeasuredInventory', numeric: true, label: 'Scale' },
        // the delete button column is automatically added at the end of those
      ];

      const sections = [
        this.renderColumns(rulesListColumns),
        this.renderColumns(trackerListColumns),
      ];
      return (
        <>
          <Panel>
            <SimpleTabs
              activeTabIndex={this.state.currentTab}
              ariaLabel='Brand Safety'
              handleChangeTab={this.handleChangeTab}
              tabs={tabs}
            />
          </Panel>
          <Panel scroll paddingTop={spacing.small} horizontalPadding>
            {this.state.currentTab === 2 ? (
              <CreativeReview classes={this.props.classes} />
            ) : (
              sections[this.state.currentTab]()
            )}
          </Panel>
        </>
      );
    }

    renderColumns(columns: IHeadCell[]) {
      return () => {
        const { classes } = this.props;
        const filteredRules = this.filterRules(this.state.rules);
        const filteredRulesLength = Array.isArray(filteredRules) ? filteredRules.length : 0;
        const filteredTrackers = this.filterTrackers(this.state.trackers);
        const filteredTrackersLength = Array.isArray(filteredTrackers)
          ? filteredTrackers.length
          : 0;
        return (
          <Panels max vertical relative minWidthMain>
            <Panel scroll>
              <Grid
                container
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                style={{ padding: '0px 16px' }}
              >
                <Grid item>
                  {/* SEARCH ENTRY */}
                  <div
                    style={{
                      backgroundColor: '#F8F8F8',
                      display: 'inline-block',
                      marginBottom: '10px',
                      minWidth: 350,
                      marginTop: 10,
                    }}
                  >
                    <IconButton classes={{ root: classes.iconButton }}>
                      <SearchIcon />
                    </IconButton>
                    <InputBase
                      classes={{ root: classes.input }}
                      placeholder={ct('Search')}
                      name={'searchFilter'} // MUST be the name of the STATE variable
                      value={this.state.searchFilter} // user search query
                      onChange={this.handleFilterChange} // handle filter change
                    />
                  </div>
                </Grid>
                <Grid item>
                  {/* BUTTON - '+ Add Rule / Tracker' */}
                  <Grid item>
                    <RowButton
                      right={
                        <ModelMediumButton
                          disabled={
                            !this.state.hasPublisherWriteRight.isAuthorized &&
                            !this.state.hasChannelWriteRight.isAuthorized &&
                            !this.state.hasBidderWriteRight.isAuthorized
                          }
                          variant='contained'
                          color='primary'
                          style={{ paddingLeft: '30px', paddingRight: '30px', width: 170 }}
                          onClick={
                            this.isCurrentTab(0)
                              ? this.addRuleButtonClickHandler
                              : this.addTrackerButtonClickHandler
                          }
                        >
                          {this.isCurrentTab(0)
                            ? ct('PlusAddItem', { item: ct('Rule') })
                            : ct('PlusAddItem', { item: ct('EventTracker') })}
                        </ModelMediumButton>
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* RULES LIST */}
              <Table className={classes.table}>
                {/* LIST COLUMN'S TITLE */}
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.id === 'name' ? 'left' : 'center'}
                        sortDirection={
                          this.state.orderBy === column.id ? this.state.orderDirection : false
                        }
                      >
                        <TableSortLabel
                          active={this.state.orderBy === column.id}
                          direction={
                            this.state.orderBy === column.id ? this.state.orderDirection : 'asc'
                          }
                          onClick={() => this.handleRequestSort(column.id)}
                          style={{ textAlign: 'center' }}
                        >
                          {/* margin left to compensate the sort label*/}
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          {/* actual title display */}
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    {/* delete button column */}
                    <TableCell align='center'>{ct('Delete')}</TableCell>
                  </TableRow>
                </TableHead>

                {/* LIST ROWS */}
                <TableBody>{this.renderListRows()}</TableBody>
              </Table>

              {/* LIST PAGINATION */}
              <TablePagination
                component='div'
                count={this.isCurrentTab(0) ? filteredRulesLength : filteredTrackersLength}
                labelDisplayedRows={({ from, to, count }) =>
                  `${count !== 0 ? from : 1}-${to} of ${count}`
                }
                page={this.isCurrentTab(0) ? this.state.page : this.state.pageTrackers}
                onPageChange={this.handleTablePaginationPageChange}
                rowsPerPage={
                  this.isCurrentTab(0) ? this.state.rowsPerPage : this.state.rowsPerPageTrackers
                }
                onChangeRowsPerPage={this.handleTablePaginationRowsPerPageChange}
              />
            </Panel>
          </Panels>
        );
      };
    }

    // ############################## MAIN RENDER ##############################
    render() {
      return (
        <Panels max vertical relative minWidthMain>
          <Panel largeHorizontalPadding smallVerticalPadding>
            <TopBar title={ct('BrandSafety')} enableUINotices />
          </Panel>
          {this.renderTabs()}

          {/* ?? DELETE CONFIRMATION POPUP ?? */}
          <ModelDialogDeleteView
            active={this.state.deletePopupActive}
            model={this.isCurrentTab(0) ? ct('Rule') : ct('EventTracker')}
            ID={this.state.deletePopupItemID}
            Name={this.state.deletePopupItemName}
            deleteModel={(id: string) => {
              return this.deleteItemWithPromise(Number(id));
            }}
            onDelete={this.onItemDelete}
            onError={this.hideDeleteDialog}
            onClose={this.hideDeleteDialog}
          />

          {/* SNACKBAR */}
          <SnackbarView
            open={!!this.state.snackbarMessage}
            error={this.state.snackbarError}
            onClose={this.snackbarClose}
          >
            {this.state.snackbarMessage}
          </SnackbarView>
        </Panels>
      );
    }
  },
);
